import './overview-pane.css';
import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Select } from '@kerfjs/ui/select';
import { StateBanner } from '@kerfjs/ui/state-banner';
import type { SafeHtml } from 'kerfjs';
import { RefreshCcwDot } from 'lucide';

import { formAcceptsAnswers } from '../../../../../shared/contract/forms.js';
import { currentPricingCheckState } from '../../../../data/pricing-prompt.js';
import {
  analysisList,
  analysisProgress,
  analysisReport,
  analysisRunning,
  type FormRow,
  selectedAnalysisId,
  workspace,
} from '../../../../data/state.js';
import { demandReadOnly } from '../../../../lib/context.js';
import { DecidedBadges } from '../decided-badges.js';
import { summaryContext } from '../summary-context.js';
import { OverviewAssumptionsSection } from './components/assumptions-section.js';
import { OverviewOpenQuestionsSection } from './components/open-questions-section.js';
import { OverviewOpportunitiesSection } from './components/opportunities-section.js';
import { OverviewProgressSection } from './components/progress-section.js';
import { OverviewReviewSection } from './components/review-section.js';
import { OverviewSummarySection } from './components/summary-section.js';
import { SupplierLeadsSection } from './components/supplier-leads-section.js';

/**
 * Summary before any pricing check (HS2-CNXFTE): a labelled Run Pricing Check
 * when the viewer can run one now, otherwise the reason it cannot run yet.
 */
function SummaryEmptyState(): SafeHtml {
  const control = currentPricingCheckState('workspace');
  const runnable = control.visible && !control.disabled;
  return (
    <EmptyState
      title="No pricing check has been run yet."
      detail={
        runnable
          ? 'Run a pricing check to benchmark each line against market prices and compare any quotations.'
          : control.visible
            ? `${control.label}.`
            : 'The procurement team runs the pricing check.'
      }
      action={
        runnable ? (
          <Button
            emphasis="primary"
            size="default"
            tone="success"
            action="run-analysis"
            icon={<LucideIcon icon={RefreshCcwDot} name="" />}
          >
            Run Pricing Check
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * Whether any of the stated assumptions' forms still takes answers: open, or a
 * skipped pricing-check clarification (HS2-54N88P).
 */
function hasOpenForm(questions: { formId: string }[]): boolean {
  const open = new Set(((workspace.value?.forms ?? []) as FormRow[]).filter(formAcceptsAnswers).map((form) => form.id));
  return questions.some((question) => open.has(question.formId));
}

/** The pricing-check Overview pane: headline figures, assumptions, opportunities. */
export function OverviewPane(): SafeHtml {
  const { lines, decidedCount, allDecided } = summaryContext();
  const report = analysisReport.value;
  const list = analysisList.value;
  const summary = report?.analysis.summary ?? null;

  let analysisContent: SafeHtml;
  if (report === null || list.length === 0) {
    analysisContent = analysisRunning.value ? (
      <OverviewProgressSection lines={lines} progress={analysisProgress.value} />
    ) : (
      <SummaryEmptyState />
    );
  } else {
    analysisContent = (
      <List gap="l">
        {analysisRunning.value ? (
          <StateBanner tone="info" title="Running a new pricing check. The previous result remains visible." />
        ) : null}
        {report.analysis.status === 'stale' ? (
          <StateBanner
            tone="warning"
            title="Inputs changed since this analysis ran — it may be out of date. Use Price Check in the toolbar to re-run it."
          />
        ) : null}
        {summary !== null && summary.missing.length > 0 ? (
          <StateBanner
            tone="warning"
            title="Pricing Check Coverage Gaps"
            detail={summary.missing.join(' ')}
            copyLayout="stacked"
          />
        ) : null}
        {/* The independent quotation review leads the Overview (FR-3.26a, HS2-XG16AM). */}
        <OverviewReviewSection review={summary?.review} />
        <SupplierLeadsSection report={report} lines={lines} />
        {summary !== null && (summary.estimateCount ?? 0) > 0 && !demandReadOnly() ? (
          <StateBanner
            tone="info"
            title={
              summary.totalAtBestQuotes === 0
                ? 'Expected prices: no quotations yet'
                : `${String(summary.estimateCount)} line${summary.estimateCount === 1 ? ' has' : 's have'} no quotation yet`
            }
            detail="Add vendor quotations, then re-run the pricing check to compare them against these estimates."
            copyLayout="stacked"
            action={
              <Button
                tone="brand"
                emphasis="primary"
                size="compact"
                action="editor-seg"
                rootAttributes={{ 'data-segment-value': 'quotes' }}
              >
                Add Quotations
              </Button>
            }
          />
        ) : null}
        {summary !== null ? <OverviewSummarySection summary={summary} verdicts={report.verdicts} /> : null}
        <OverviewOpenQuestionsSection
          questions={summary?.openQuestions ?? []}
          answerable={!demandReadOnly() && hasOpenForm(summary?.openQuestions ?? [])}
        />
        <OverviewAssumptionsSection
          assumptions={report.analysis.assumptionsSnapshot ?? {}}
          base={summary?.base ?? 'PKR'}
        />
        {summary !== null ? (
          <OverviewOpportunitiesSection opportunities={summary.topOpportunities} base={summary.base} />
        ) : null}
      </List>
    );
  }

  return (
    <List className="overview-pane" gap="m">
      <Row vAlign="middle" gap="xs" wrap controlInsets="rl">
        {report !== null && list.length > 1 ? (
          <Select
            name="analysis-version"
            value={selectedAnalysisId.value}
            ariaLabel="Pricing-Check Version"
            presentation="navigation"
            size="compact"
            labelMaxWidth={240}
            choices={list.map((version) => ({
              value: version.id,
              label: `v${String(version.version)} · ${version.status}`,
            }))}
          />
        ) : null}
        <DecidedBadges decidedCount={decidedCount} total={lines.length} allDecided={allDecided} />
      </Row>
      {analysisContent}
    </List>
  );
}
